// Palette = N random tiles from the segments table. N is dynamic, set by
// the Palette component based on its measured width (more screen width =
// more tiles). Auto-refills with a same-source-image sibling when a tile
// is dragged out; falls back to a random pick if no untouched siblings
// remain.

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchPalette, fetchSiblings } from "../api.js";

export function usePalette({ size, excludeIds }) {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const tilesRef = useRef(tiles);
  tilesRef.current = tiles;
  const excludeIdsRef = useRef(excludeIds);
  excludeIdsRef.current = excludeIds;

  const reshuffle = useCallback(async () => {
    const n = Math.max(1, size || 8);
    setLoading(true);
    try {
      const fresh = await fetchPalette({ n, exclude: excludeIdsRef.current });
      setTiles(padToSize(fresh, n));
    } catch {} finally {
      setLoading(false);
    }
  }, [size]);

  // One effect that handles both the initial load and any subsequent size
  // change. Critical: this effect's cleanup gets called on every size
  // change AND on StrictMode's intentional dev-time double-mount. So we
  // make sure both branches (filled and skipped) clear `loading` —
  // otherwise the dice button stays disabled forever.
  useEffect(() => {
    if (!size || size < 1) return;
    let cancelled = false;
    const current = tilesRef.current;

    if (current.length === size) {
      setLoading(false);
      return;
    }
    if (current.length > size) {
      setTiles(current.slice(0, size));
      setLoading(false);
      return;
    }

    // Need more tiles. Preserve what we already have, fetch the delta.
    setLoading(true);
    const have = current.map((t) => (t ? t.id : null)).filter((n) => Number.isFinite(n));
    const want = size - current.length;
    fetchPalette({ n: want, exclude: [...(excludeIdsRef.current || []), ...have] })
      .then((fresh) => {
        if (cancelled) return;
        setTiles((p) => padToSize([...p, ...fresh], Math.max(size, p.length + fresh.length)));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [size]);

  // Replace a slot with a sibling of the dragged-out tile, or a random
  // pick if no siblings are available.
  const removeAndRefill = useCallback(
    async (tileIdx, draggedSourceId, draggedSegmentId) => {
      // Optimistically blank the slot so the user sees the change.
      setTiles((t) => {
        const next = t.slice();
        next[tileIdx] = null;
        return next;
      });

      const currentTileIds = tilesRef.current
        .filter((t, i) => t && i !== tileIdx)
        .map((t) => t.id);
      const fullExclude = [...(excludeIdsRef.current || []), ...currentTileIds, draggedSegmentId]
        .filter(Number.isFinite);

      let replacement = null;
      try {
        const siblings = await fetchSiblings(draggedSegmentId, {
          exclude: fullExclude,
          n: 1,
        });
        if (siblings.length > 0) replacement = siblings[0];
      } catch {}
      if (!replacement) {
        try {
          const random = await fetchPalette({ n: 1, exclude: fullExclude });
          if (random.length > 0) replacement = random[0];
        } catch {}
      }

      setTiles((t) => {
        const next = t.slice();
        next[tileIdx] = replacement;
        return next;
      });
    },
    [],
  );

  // Inject siblings of an arbitrary segment into the palette (replace).
  const injectSiblings = useCallback(
    async (anchorSegmentId) => {
      const n = Math.max(1, size || 8);
      setLoading(true);
      try {
        const siblings = await fetchSiblings(anchorSegmentId, {
          exclude: excludeIdsRef.current,
          n,
        });
        setTiles(padToSize(siblings, n));
      } catch {} finally {
        setLoading(false);
      }
    },
    [size],
  );

  return { tiles, loading, reshuffle, removeAndRefill, injectSiblings, setTiles };
}

function padToSize(arr, size) {
  const next = arr.slice(0, size);
  while (next.length < size) next.push(null);
  return next;
}
